/// <amd-module name="Repos/Common/ReposRoutingService" />
import { GitVersionDescriptor } from "Repos/Common/Contracts/Git";
import { getVersionString } from "Repos/Common/Util/Version";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { routeUrl } from "VSS/Platform/Location";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { getRefNameFromBranchName } from "./Util/Ref";
import { IVCViewModelDataProviderService } from "./VCViewModelDataProviderService";

export interface IReposRoutingService extends IVssService {
    /**
     * Gets the URL of the branches pages for comaring 2 versions
     *
     * @param projectName Name of the project
     * @param baseVersion VersionString for the base version (like "GBmaster")
     * @param targetVersion VersionString for the target version (like "GBmaster")
     * @param filesTab Option to make it route to the files tab
     */
    getBranchCompareUrl(
        repositoryName: string,
        projectName: string,
        baseVersion: GitVersionDescriptor,
        targetVersion: GitVersionDescriptor,
        tab?: string
    ): string;

    /**
     * Gets the URL of the branches hub
     *
     * @param repositoryName Repository name
     * @param projectName Project name
     */
    getBranchesUrl(repositoryName: string, projectName: string): string;

    /**
     * Gets the URL of the branch policy page
     *
     * @param repositoryName Repository name
     * @param projectName Project name
     * @param branchRefName Branch name
     * @param repositoryId Id of the repository
     */
    getBranchPolicyUrl(repositoryName: string, projectName: string, branchRefName: string, repositoryId?: string): string;

    /**
     * Gets the URL of the branch security page
     *
     * @param repositoryId Id of the repository
     * @param projectName Project name
     * @param branchName Branch name
     */
    getBranchSecurityUrl(repositoryId: string, projectName: string, branchName: string): string;

    /**
     * Gets the URL of the commit details page for the given commit
     *
     * @param commitId Commit id (SHA1)
     * @param repositoryName Name of the repository
     * @param projectName Name of the project
     * @param refName Optional refName of the branch to target
     * @param path Optional path to select by default when opening the commit
     * @param action Specific tab to select by default
     */
    getCommitUrl(repositoryName: string, projectName: string, commitId: string, refName?: string, path?: string, action?: string): string;

    /**
     * Gets the URL of the commits hub
     *
     * @param repositoryName Name of the repository
     * @param projectName Name of the project
     * @param version Specific version to load commits from
     */
    getCommitsUrl(repositoryName: string, projectName: string, version?: GitVersionDescriptor): string;

    /**
     * Gets the URL to open the Blame view for the specified file
     *
     * @param repositoryName Name of the repository
     * @param projectName Name of the project
     * @param path Path to the file
     * @param version Specific version of the file
     */
    getFilesBlameUrl(repositoryName: string, projectName: string, path: string, version?: GitVersionDescriptor): string;

    /**
     * Gets the URL to open the compare view on the files hub for the specified file
     *
     * @param repositoryName Name of the repository
     * @param projectName Name of the project
     * @param path Path to the file
     * @param baseVersion Base version for compare
     * @param originalVersion Original version for compare
     * @param modifiedVersion Modified version for compare
     */
    getFilesCompareUrl(
        repositoryName: string,
        projectName: string,
        path: string,
        baseVersion: GitVersionDescriptor,
        originalVersion: GitVersionDescriptor,
        modifiedVersion: GitVersionDescriptor
    ): string;

    /**
     * Gets the URL to open the History view for the specified path
     *
     * @param repositoryName Name of the repository
     * @param projectName Name of the project
     * @param path Path to the item
     * @param version Specific version of the item
     */
    getFilesHistoryUrl(repositoryName: string, projectName: string, path?: string, version?: GitVersionDescriptor): string;

    /**
     * Gets the URL to the files hub
     *
     * @param repositoryName Name of the repository
     * @param projectName Name of the project
     * @param path Path of the item to show
     * @param version Specific version to use
     * @param anchor Anchor to scroll into view when opening the file
     */
    getFilesUrl(repositoryName: string, projectName: string, path?: string, version?: GitVersionDescriptor, anchor?: string): string;

    /**
     * Gets the URL of the Create Pull Request page
     *
     * @param repositoryName Repository name
     * @param projectName Project name
     * @param sourceRef Name of the initial source ref to select (e.g. branch name). Name after refs/heads/
     * @param targetRef Name of the initial target ref to select (e.g. branch name). Name after refs/heads/
     * @param sourceRepositoryId Id of the source repository
     * @param targetRepositoryId Id of the target repository
     * @param cherryPickId Id of the PR we're cherry picking from (in order to add a note to description)
     * @param revertId Id of the PR we're reverting (in order to add a note to description)
     * @param cherryPickCommit Hash of the commit we're cherry picking (in order to add a note to description)
     * @param revertCommit Hash of the commit we're reverting (in order to add a note to description)
     */
    getPullRequestCreateUrl(
        repositoryName?: string,
        projectName?: string,
        sourceRef?: string,
        targetRef?: string,
        sourceRepositoryId?: string,
        targetRepositoryId?: string,
        cherryPickId?: number,
        revertId?: number,
        cherryPickCommit?: string,
        revertCommit?: string
    ): string;

    /**
     * Gets the URL for the specified pull request
     * @param id Pull request id
     * @param repositoryName Repository name
     * @param projectName Project name
     * @param tabName Pull request tab to select
     * @param path to select
     * @param iteration to select
     * @param baseIteration to select
     * @param discussionId to select
     */
    getPullRequestUrl(
        repositoryName: string,
        projectName: string,
        id: number,
        tabName?: string,
        path?: string,
        iteration?: string,
        baseIteration?: string,
        discussionId?: number
    ): string;

    /**
     * Gets the URL to the pull requests page
     *
     * @param tabName Pull request tab to select
     * @param createdByUserId Filter pull requests created by this user
     * @param assignedToUserId Filter pull requests assigned to this user
     * @param targetRefName Filter pull requests that target the given ref
     */
    getPullRequestsUrl(tabName?: string, createdByUserId?: string, assignedToUserId?: string, targetRefName?: string): string;

    /**
     * Get the URL for the specified push
     * @param repositoryName Repository name
     * @param projectName Project name
     * @param id Push id
     */
    getPushUrl(repositoryName: string, projectName: string, id: number): string;

    /**
     * Gets the URL of the pushes hub
     *
     * @param repositoryName Repository name
     * @param projectName Project name
     * @param routeValues Optional route values to add as query parameters
     */
    getPushesUrl(repositoryName: string, projectName: string, routeValues?: { [key: string]: string }): string;

    /**
     * Gets the URL for the specified repository for the currently active hub
     *
     * @param repositoryName Name of the repository
     * @param projectName Name of the project
     * @param version Specific version of the repo
     */
    getSwitchRepositoryURL(repositoryName: string | null, projectName?: string, version?: GitVersionDescriptor): string;
}

const enum RouteIds {
    AdvancedSecurity = "ms.vss-advsec.alerts-hub-route",

    Branches = "ms.vss-code-web.branches-route",
    BranchesCompare = "ms.vss-code-web.branch-compare-route",

    Commit = "ms.vss-code-web.commit-route",
    Commits = "ms.vss-code-web.commits-route",

    TfvcFiles = "ms.vss-code-web.tfvc-files-route",
    GitFiles = "ms.vss-code-web.files-route-git",

    Push = "ms.vss-code-web.push-details-route",
    Pushes = "ms.vss-code-web.pushes-route",
    Tags = "ms.vss-code-web.tags-route",

    CreatePullRequest = "ms.vss-code-web.pull-request-create-route",
    PullRequests = "ms.vss-code-web.lwp-prs-route",
    PullRequest = "ms.vss-code-web.pull-request-review-route",

    AdminHub = "ms.vss-admin-web.project-admin-hub-route",

    GitOverview = "ms.vss-code-web.repository-overview-git-route",
    TfvcOverview = "ms.vss-code-web.repository-overview-tfvc-route"
}

const DefaultHubRoutes: { [hubId: string]: string } = {
    "ms.vss-code-web.git-repository-overview": RouteIds.GitOverview,
    "ms.vss-code-web.files-hub": RouteIds.GitFiles,
    "ms.vss-code-web.branches-hub": RouteIds.Branches,
    "ms.vss-code-web.tags-hub": RouteIds.Tags,
    "ms.vss-code-web.pushes-hub": RouteIds.Pushes,
    "ms.vss-code-web.commits-hub": RouteIds.Commits,
    "ms.vss-code-web.new-prs-hub": RouteIds.PullRequests,
    "ms.vss-advsec.alerts-hub": RouteIds.AdvancedSecurity
};

class ReposRoutingService extends VssService implements IReposRoutingService {
    private repositoryId: string;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);

        const vcViewModelDataProviderService = this.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        const repository = vcViewModelDataProviderService.getRepository();
        this.repositoryId = repository ? repository.id : "";
    }

    public getBranchCompareUrl(
        repositoryName: string,
        projectName: string,
        baseVersion: GitVersionDescriptor,
        targetVersion: GitVersionDescriptor,
        tab?: string
    ): string {
        const routeValues: { [key: string]: string } = {};
        routeValues["project"] = projectName;
        routeValues["vc.GitRepositoryName"] = repositoryName;
        routeValues["baseVersion"] = getVersionString(baseVersion);
        routeValues["targetVersion"] = getVersionString(targetVersion);
        routeValues["_a"] = tab ? tab : "commits";

        return routeUrl(this.pageContext, RouteIds.BranchesCompare, routeValues);
    }

    public getBranchesUrl(repositoryName: string, projectName: string): string {
        const routeValues: { [key: string]: string } = {};
        routeValues["project"] = projectName;
        routeValues["vc.GitRepositoryName"] = repositoryName;

        return routeUrl(this.pageContext, RouteIds.Branches, routeValues);
    }

    public getBranchPolicyUrl(repositoryName: string, projectName: string, branchRefName: string, repositoryId?: string): string {
        repositoryId = repositoryId || this.repositoryId;

        let branchPolicyUrl = "";
        const routeValues: { [key: string]: string } = {};
        routeValues["project"] = projectName;
        routeValues["adminPivot"] = "repositories";
        routeValues["_a"] = "policiesMid";
        routeValues["repo"] = repositoryId;
        routeValues["refs"] = branchRefName;

        branchPolicyUrl = routeUrl(this.pageContext, RouteIds.AdminHub, routeValues);

        return branchPolicyUrl;
    }

    public getBranchSecurityUrl(repositoryId: string, projectName: string, branchName: string): string {
        const branchRefName = getRefNameFromBranchName(branchName);
        const routeValues: { [key: string]: string } = {};
        routeValues["project"] = projectName;
        routeValues["adminPivot"] = "repositories";
        routeValues["_a"] = "permissionsMid";
        routeValues["repo"] = repositoryId;
        routeValues["refs"] = branchRefName;

        return routeUrl(this.pageContext, RouteIds.AdminHub, routeValues);
    }

    public getCommitUrl(repositoryName: string, projectName: string, commitId: string, refName?: string, path?: string, action?: string): string {
        const routeValues: { [key: string]: string } = {};
        routeValues["project"] = projectName;
        routeValues["vc.GitRepositoryName"] = repositoryName;
        routeValues["parameters"] = commitId;

        if (refName) {
            routeValues["refName"] = refName;
        }

        if (path) {
            routeValues["path"] = path;
            routeValues["_a"] = action || "contents";
        }

        return routeUrl(this.pageContext, RouteIds.Commit, routeValues);
    }

    public getCommitsUrl(repositoryName: string, projectName: string, version?: GitVersionDescriptor): string {
        const routeValues: { [key: string]: string } = {};
        routeValues["project"] = projectName;
        routeValues["vc.GitRepositoryName"] = repositoryName;
        if (version) {
            routeValues["itemVersion"] = getVersionString(version);
        }

        return routeUrl(this.pageContext, RouteIds.Commits, routeValues);
    }

    public getFilesBlameUrl(repositoryName: string, projectName: string, path: string, version?: GitVersionDescriptor): string {
        return this.getFilesUrlForTab(repositoryName, projectName, path, version, "blame");
    }

    public getFilesCompareUrl(
        repositoryName: string,
        projectName: string,
        path: string,
        baseVersion: GitVersionDescriptor,
        originalVersion: GitVersionDescriptor,
        modifiedVersion: GitVersionDescriptor
    ): string {
        const routeValues: { [key: string]: string } = {};
        routeValues["project"] = projectName;
        routeValues["vc.GitRepositoryName"] = repositoryName;
        routeValues["_a"] = "compare";
        routeValues["oversion"] = getVersionString(originalVersion);
        routeValues["mversion"] = getVersionString(modifiedVersion);
        routeValues["version"] = getVersionString(baseVersion);
        routeValues["path"] = path;

        return routeUrl(this.pageContext, "ms.vss-code-web.files-route", routeValues);
    }

    public getFilesHistoryUrl(repositoryName: string, projectName: string, path?: string, version?: GitVersionDescriptor): string {
        return this.getFilesUrlForTab(repositoryName, projectName, path, version, "history");
    }

    public getFilesUrl(repositoryName: string, projectName: string, path?: string, version?: GitVersionDescriptor, anchor?: string): string {
        return this.getFilesUrlForTab(repositoryName, projectName, path, version, undefined, anchor);
    }

    public getPullRequestCreateUrl(
        repositoryName?: string,
        projectName?: string,
        sourceRef?: string,
        targetRef?: string,
        sourceRepositoryId?: string,
        targetRepositoryId?: string,
        cherryPickId?: number,
        revertId?: number,
        cherryPickCommit?: string,
        revertCommit?: string
    ): string {
        const navigationService = this.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        const currentRouteValues = navigationService.getRouteValues();
        const routeValues: { [key: string]: string } = {};

        routeValues["project"] = projectName || currentRouteValues["project"];

        const repositoryNameParam = repositoryName || currentRouteValues["GitRepositoryName"];
        if (repositoryNameParam) {
            routeValues["vc.GitRepositoryName"] = repositoryNameParam;
        }

        if (sourceRef) {
            routeValues["sourceRef"] = sourceRef;
        }
        if (targetRef) {
            routeValues["targetRef"] = targetRef;
        }
        if (sourceRepositoryId) {
            routeValues["sourceRepositoryId"] = sourceRepositoryId;
        }
        if (targetRepositoryId) {
            routeValues["targetRepositoryId"] = targetRepositoryId;
        }
        if (cherryPickId) {
            routeValues["cherryPickId"] = cherryPickId.toString();
        }
        if (revertId) {
            routeValues["revertId"] = revertId.toString();
        }
        if (cherryPickCommit) {
            routeValues["cherryPickCommit"] = cherryPickCommit;
        }
        if (revertCommit) {
            routeValues["revertCommit"] = revertCommit;
        }

        return routeUrl(this.pageContext, RouteIds.CreatePullRequest, routeValues);
    }

    public getPullRequestUrl(
        repositoryName: string,
        projectName: string,
        id: number,
        tabName?: string,
        path?: string,
        iteration?: string,
        baseIteration?: string,
        discussionId?: number
    ): string {
        const routeValues: { [key: string]: string } = {};
        routeValues["project"] = projectName;
        routeValues["vc.GitRepositoryName"] = repositoryName;
        routeValues["parameters"] = id.toString();

        if (tabName) {
            routeValues["_a"] = tabName;
        }

        if (path) {
            routeValues["path"] = path;
        }

        if (iteration) {
            routeValues["iteration"] = iteration;
        }

        if (baseIteration) {
            routeValues["base"] = baseIteration;
        }

        if (discussionId) {
            routeValues["discussionId"] = discussionId.toString();
        }

        return routeUrl(this.pageContext, RouteIds.PullRequest, routeValues);
    }

    public getPullRequestsUrl(tabName?: string, createdByUserId?: string, assignedToUserId?: string, targetRefName?: string): string {
        const navigationService = this.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        const currentRouteValues = navigationService.getRouteValues();
        const routeValues: { [key: string]: string } = {};
        routeValues["project"] = currentRouteValues["project"];

        if (tabName) {
            routeValues["_a"] = tabName;
        }

        if (currentRouteValues["GitRepositoryName"]) {
            routeValues["vc.GitRepositoryName"] = currentRouteValues["GitRepositoryName"];
        }

        if (createdByUserId) {
            routeValues["createdBy"] = createdByUserId;
        }

        if (assignedToUserId) {
            routeValues["assignedTo"] = assignedToUserId;
        }

        if (targetRefName) {
            routeValues["targetRefName"] = targetRefName;
        }

        return routeUrl(this.pageContext, RouteIds.PullRequests, routeValues);
    }

    public getPushUrl(repositoryName: string, projectName: string, id: number): string {
        const routeValues: { [key: string]: string } = {};
        routeValues["project"] = projectName;
        routeValues["vc.GitRepositoryName"] = repositoryName;
        routeValues["parameters"] = id.toString();

        return routeUrl(this.pageContext, RouteIds.Push, routeValues);
    }

    public getPushesUrl(repositoryName: string, projectName: string, optionalRouteValues?: { [key: string]: string }): string {
        const routeValues = { ...optionalRouteValues } as { [key: string]: string };
        routeValues["project"] = projectName;
        routeValues["vc.GitRepositoryName"] = repositoryName;

        return routeUrl(this.pageContext, RouteIds.Pushes, routeValues);
    }

    public getSwitchRepositoryURL(repositoryName: string | null, projectName?: string, version?: GitVersionDescriptor): string {
        const routeValues: { [key: string]: string } = {};
        let routeId;

        if (repositoryName) {
            const navigationService = this.pageContext.getService<IVssNavigationService>("IVssNavigationService");
            const currentHubId = navigationService.getDisplayedNavigation()[3];
            routeId = DefaultHubRoutes[currentHubId] || RouteIds.GitFiles;
        } else {
            routeId = RouteIds.TfvcFiles;
        }
        if (projectName) {
            routeValues["project"] = projectName;
        }
        if (repositoryName) {
            routeValues["vc.GitRepositoryName"] = repositoryName;
        }

        if (version) {
            routeValues["version"] = getVersionString(version);
        }

        return routeUrl(this.pageContext, routeId, routeValues);
    }

    private getFilesUrlForTab(
        repositoryName: string,
        projectName: string,
        path: string | undefined,
        version: GitVersionDescriptor | undefined,
        tab: string | undefined,
        anchor?: string
    ): string {
        const routeValues: { [key: string]: string } = {};
        routeValues["project"] = projectName;
        routeValues["vc.GitRepositoryName"] = repositoryName;

        if (tab) {
            routeValues["_a"] = tab;
        }

        if (path) {
            routeValues["path"] = path;
        }

        if (version) {
            routeValues["version"] = getVersionString(version);
        }

        if (anchor) {
            routeValues["anchor"] = anchor;
        }

        return routeUrl(this.pageContext, RouteIds.GitFiles, routeValues);
    }
}

const serviceName = "IReposRoutingService";

export function getReposRoutingService(pageContext: IVssPageContext): IReposRoutingService {
    return pageContext.getService<IReposRoutingService>(serviceName);
}

Services.add(serviceName, { serviceFactory: ReposRoutingService });
